<template>
  <div class="workbench-v2">
    <div class="layout">
      <aside class="side-menu">
        <div class="menu-title">客服工作台</div>
        <div class="menu-item" :class="{ active: activeModule === 'data' }" @click="activeModule = 'data'">
          数据概览
        </div>
        <div class="menu-item" :class="{ active: activeModule === 'sessions' }" @click="activeModule = 'sessions'">
          会话列表
          <el-badge v-if="unreadTotal" :value="unreadTotal" class="menu-badge" />
        </div>
      </aside>

      <section class="content">
        <template v-if="activeModule === 'data'">
          <el-row :gutter="12" class="summary-row">
            <el-col :span="6">
              <div class="summary-card">
                <div class="label">连接状态</div>
                <div class="value" :class="{ online: wsConnected }">{{ wsConnected ? '实时在线' : '轮询模式' }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-card">
                <div class="label">全部会话</div>
                <div class="value">{{ conversations.length }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-card">
                <div class="label">进行中</div>
                <div class="value">{{ countByStatus('active') }}</div>
              </div>
            </el-col>
            <el-col :span="6">
              <div class="summary-card">
                <div class="label">未读提醒</div>
                <div class="value">{{ unreadTotal }}</div>
              </div>
            </el-col>
          </el-row>

          <el-card>
            <div slot="header" class="card-header">
              <div>
                <span>快捷回复</span>
                <span class="card-subhint">会话列表右侧聊天区也可直接使用</span>
              </div>
            </div>
            <div class="quick-reply-list">
              <el-button
                v-for="item in quickReplyList"
                :key="item.id"
                size="mini"
                plain
                @click="appendReply(item.content)"
              >
                {{ item.title }}
              </el-button>
            </div>
          </el-card>
        </template>

        <template v-else>
          <el-row :gutter="12">
            <el-col :span="11">
              <el-card class="session-card">
                <div slot="header" class="card-header">
                  <span>会话列表</span>
                  <el-button size="mini" @click="loadConversations">刷新</el-button>
                </div>
                <div class="filters">
                  <el-input v-model="keyword" size="small" clearable placeholder="搜索会话ID或访客" />
                  <el-select v-model="statusFilter" size="small" placeholder="状态">
                    <el-option label="全部" value="all" />
                    <el-option label="进行中" value="active" />
                    <el-option label="排队中" value="queued" />
                    <el-option label="已结束" value="closed" />
                  </el-select>
                </div>
                <div class="sort-tools">
                  <el-switch
                    v-model="prioritizeUnread"
                    active-text="新消息优先置顶"
                    inactive-text="按时间排序"
                    active-color="#409eff"
                  />
                </div>

                <el-table
                  ref="convTable"
                  :data="paginatedConversations"
                  row-key="id"
                  height="520"
                  highlight-current-row
                  @current-change="selectConversation"
                >
                  <el-table-column prop="id" label="会话ID" width="78" />
                  <el-table-column prop="visitor_label" label="访客" min-width="120" show-overflow-tooltip />
                  <el-table-column label="状态" width="96">
                    <template slot-scope="{ row }">
                      <el-tag :type="statusTagType(row.status)" size="mini">{{ statusLabel(row.status) }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="新消息" width="76">
                    <template slot-scope="{ row }">
                      <span v-if="unreadMap[row.id]" class="unread-bell">🔔</span>
                      <span v-else style="color:#909399;">-</span>
                    </template>
                  </el-table-column>
                  <el-table-column prop="created_at" label="创建时间" min-width="146" />
                  <el-table-column label="操作" width="96" fixed="right" align="right" header-align="right">
                    <template slot-scope="{ row }">
                      <div class="op-cell">
                        <el-button size="mini" type="primary" plain @click.stop="enterConversation(row)">进入会话</el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
                <div class="pager-wrap">
                  <el-pagination
                    background
                    layout="prev, pager, next"
                    :page-size="pageSize"
                    :current-page.sync="currentPage"
                    :total="sortedFilteredConversations.length"
                    @current-change="onPageChange"
                  />
                </div>
              </el-card>
            </el-col>

            <el-col :span="13">
              <el-card class="chat-card">
                <div slot="header" class="chat-header">
                  <div>
                    <div class="chat-title">会话 {{ currentConversationId ? '#' + currentConversationId : '-' }}</div>
                    <div class="chat-subtitle">
                      <span v-if="currentConversation && currentConversation.visitor_label">访客：{{ currentConversation.visitor_label }}</span>
                      <span v-if="currentConversation"> | 创建：{{ currentConversation.created_at || '-' }}</span>
                      <span v-if="currentConversation"> | 分配：{{ currentConversation.assigned_at || '-' }}</span>
                      <span v-if="currentConversation && currentConversation.closed_at"> | 结束：{{ currentConversation.closed_at }}</span>
                    </div>
                  </div>
                  <div class="chat-actions">
                    <el-select v-model="transferTarget" placeholder="转接给" size="mini" style="width:150px;">
                      <el-option v-for="item in colleagueList" :key="item.id" :label="item.display_name" :value="item.id" />
                    </el-select>
                    <el-button size="mini" @click="transfer" :disabled="isClosedConversation || !currentConversationId">转接</el-button>
                    <el-button size="mini" type="danger" @click="closeCurrent" :disabled="isClosedConversation || !currentConversationId">结束</el-button>
                  </div>
                </div>

                <div ref="msgBox" class="msg-box">
                  <div v-for="item in messages" :key="item.id" class="msg-row" :class="{ self: item.sender_type === 'agent' }">
                    <div class="msg-bubble">
                      <div class="msg-body">
                        <div v-if="item.content_type === 'text'" class="msg-content">{{ item.content }}</div>
                        <template v-else>
                          <img
                            v-if="item.content_type === 'image' && item.file_url"
                            :src="mediaUrl(item.file_url)"
                            class="msg-media img"
                            alt=""
                          />
                          <video
                            v-else-if="item.content_type === 'video' && item.file_url"
                            :src="mediaUrl(item.file_url)"
                            class="msg-media video"
                            controls
                          />
                          <a
                            v-else-if="item.content_type === 'file' && item.file_url"
                            :href="mediaUrl(item.file_url)"
                            target="_blank"
                            rel="noopener"
                            class="msg-file"
                          >{{ item.content || '下载附件' }}</a>
                          <div
                            v-if="showMediaCaption(item)"
                            class="msg-content msg-caption"
                          >{{ item.content }}</div>
                        </template>
                      </div>
                      <div class="msg-time">{{ item.created_at || '' }}</div>
                    </div>
                  </div>
                  <el-empty v-if="currentConversationId && !messages.length" :image-size="80" description="暂无消息" />
                  <el-empty v-if="!currentConversationId" :image-size="80" description="请选择会话并点击进入" />
                </div>

                <div v-if="quickReplyList.length" class="quick-reply-inline">
                  <span class="quick-reply-label">快捷回复</span>
                  <div class="quick-reply-chips">
                    <el-button
                      v-for="item in quickReplyList"
                      :key="'qr-' + item.id"
                      size="mini"
                      plain
                      type="primary"
                      :disabled="!currentConversationId"
                      @click="appendReply(item.content)"
                    >
                      {{ item.title }}
                    </el-button>
                  </div>
                </div>

                <div class="faq-inline" v-if="currentConversationId">
                  <span class="quick-reply-label">常见问题</span>
                  <div class="faq-inline-main">
                    <div class="faq-search-row">
                      <el-input
                        v-model="faqQuery"
                        size="mini"
                        clearable
                        placeholder="关键词匹配标题，空格表示多处都要匹配"
                        style="width:260px;"
                        @keyup.enter.native="runFaqSearch"
                      />
                      <el-button size="mini" @click="runFaqSearch" :loading="faqSearchLoading">搜索</el-button>
                    </div>
                    <el-checkbox-group v-if="faqResults.length" v-model="faqSelectedIds" class="faq-check-group">
                      <el-checkbox v-for="item in faqResults" :key="'faq-' + item.id" :label="item.id">{{ item.title }}</el-checkbox>
                    </el-checkbox-group>
                    <div class="faq-push-row">
                      <el-button
                        size="mini"
                        type="success"
                        plain
                        @click="pushFaqToVisitor"
                        :disabled="!faqSelectedIds.length"
                      >推送给访客选择</el-button>
                      <span class="faq-push-hint">访客将在聊天窗口看到可选标题，点选后查看答案</span>
                    </div>
                  </div>
                </div>

                <div class="input-area">
                  <input ref="agentFile" type="file" class="hidden-file" @change="onAgentFile" />
                  <el-input
                    type="textarea"
                    :rows="2"
                    resize="none"
                    v-model="replyText"
                    :disabled="!currentConversationId"
                    :placeholder="replyPlaceholder"
                    @keydown.native="handleInputKeydown"
                  />
                  <div class="send-area">
                    <span class="tips">会话列表内可直接点快捷回复填入输入框</span>
                    <div class="send-actions">
                      <el-button size="small" @click="$refs.agentFile.click()" :disabled="!currentConversationId">附件</el-button>
                      <el-button type="primary" @click="sendReply" :disabled="!currentConversationId">发送</el-button>
                    </div>
                  </div>
                </div>
              </el-card>
            </el-col>
          </el-row>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import {
  heartbeat,
  listConversations,
  conversationMessages,
  replyConversation,
  closeConversation,
  transferConversation,
  quickReplies,
  colleagues,
  agentUpload,
  searchFaq,
  pushConversationFaq,
  API_BASE
} from '../api/agent';
import { WS_URL } from '../config/endpoints';
import { getToken } from '../utils/auth';

export default {
  data() {
    return {
      conversations: [],
      currentConversationId: null,
      messages: [],
      cursor: 0,
      replyText: '',
      quickReplyList: [],
      colleagueList: [],
      transferTarget: null,
      activeModule: 'sessions',
      keyword: '',
      statusFilter: 'all',
      prioritizeUnread: true,
      currentPage: 1,
      pageSize: 10,
      unreadMap: {},
      ws: null,
      wsConnected: false,
      timer: null,
      reconnectTimer: null,
      originalTitle: '',
      titleFlashTimer: null,
      titleFlashToggle: false,
      notifyAudioCtx: null,
      faqQuery: '',
      faqResults: [],
      faqSelectedIds: [],
      faqSearchLoading: false
    };
  },
  computed: {
    currentConversation() {
      return this.conversations.find(v => v.id === this.currentConversationId) || null;
    },
    isClosedConversation() {
      return !!(this.currentConversation && this.currentConversation.status === 'closed');
    },
    replyPlaceholder() {
      if (!this.currentConversationId) return '请先选择会话';
      if (this.isClosedConversation) return '会话已结束，消息将作为留言保存，访客下次打开可查看（Enter发送 / Shift+Enter换行）';
      return '输入消息，Enter发送 / Shift+Enter换行';
    },
    filteredConversations() {
      return this.conversations.filter(item => {
        if (this.statusFilter !== 'all' && item.status !== this.statusFilter) return false;
        if (!this.keyword.trim()) return true;
        const q = this.keyword.trim();
        if (String(item.id).includes(q)) return true;
        const label = item.visitor_label ? String(item.visitor_label) : '';
        return label.toLowerCase().includes(q.toLowerCase());
      });
    },
    sortedFilteredConversations() {
      return [...this.filteredConversations].sort((a, b) => {
        if (this.prioritizeUnread) {
          const ua = Number(this.unreadMap[a.id] || 0);
          const ub = Number(this.unreadMap[b.id] || 0);
          if (ub !== ua) return ub - ua;
        }
        const ta = new Date(a.created_at || 0).getTime();
        const tb = new Date(b.created_at || 0).getTime();
        if (tb !== ta) return tb - ta;
        return (b.id || 0) - (a.id || 0);
      });
    },
    paginatedConversations() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.sortedFilteredConversations.slice(start, start + this.pageSize);
    },
    unreadTotal() {
      return Object.values(this.unreadMap).reduce((sum, v) => sum + Number(v || 0), 0);
    }
  },
  methods: {
    mediaUrl(path) {
      if (!path) return '';
      if (/^https?:\/\//i.test(path)) return path;
      return (API_BASE || '') + path;
    },
    showMediaCaption(item) {
      if (!item || !item.file_url) return false;
      if (item.content_type !== 'image' && item.content_type !== 'video') return false;
      const c = (item.content || '').trim();
      if (!c || c === '[附件]') return false;
      if (item.file_name && c === item.file_name) return false;
      return true;
    },
    async onAgentFile(e) {
      const input = e.target;
      const file = input.files && input.files[0];
      input.value = '';
      if (!file || !this.currentConversationId) return;
      try {
        const res = await agentUpload(file);
        const meta = res.data && res.data.data;
        if (!meta || !meta.url) return;
        const body = {
          content_type: meta.content_type,
          content: meta.name || '[附件]',
          file_url: meta.url,
          file_name: meta.name || '',
          file_mime: meta.mime || '',
          file_size: meta.size || 0
        };
        const { data: r2 } = await replyConversation(this.currentConversationId, body);
        if (r2 && r2.data) this.upsertMessages([r2.data]);
        await this.loadMessages();
      } catch (err) {
        this.$message.error('上传或发送失败');
      }
    },
    countByStatus(status) {
      return this.conversations.filter(v => v.status === status).length;
    },
    statusTagType(status) {
      if (status === 'active') return 'success';
      if (status === 'queued') return 'warning';
      if (status === 'closed') return 'info';
      return '';
    },
    statusLabel(status) {
      if (status === 'active') return '进行中';
      if (status === 'queued') return '排队中';
      if (status === 'closed') return '已结束';
      return status || '-';
    },
    async loadConversations() {
      const { data } = await listConversations();
      this.conversations = data.data || [];
      this.ensurePageInRange();
      if (!this.conversations.length) {
        this.currentConversationId = null;
        this.messages = [];
        this.cursor = 0;
        return;
      }

      const exists = this.conversations.some(v => v.id === this.currentConversationId);
      if (!exists) this.currentConversationId = null;
      if (!this.currentConversationId && this.conversations.length) {
        await this.selectConversation(this.conversations[0]);
      }
    },
    async selectConversation(row) {
      if (!row) return;
      this.currentConversationId = row.id;
      this.$set(this.unreadMap, row.id, 0);
      this.cursor = 0;
      this.messages = [];
      this.faqQuery = '';
      this.faqResults = [];
      this.faqSelectedIds = [];
      await this.loadMessages();
    },
    async enterConversation(row) {
      this.activeModule = 'sessions';
      this.locateConversationPage(row.id);
      await this.selectConversation(row);
      this.$nextTick(this.highlightCurrentRow);
    },
    async loadMessages() {
      if (!this.currentConversationId) return;
      const { data } = await conversationMessages(this.currentConversationId, this.cursor);
      const list = data.data || [];
      if (list.length) this.upsertMessages(list);
    },
    upsertMessages(list) {
      const map = {};
      this.messages.forEach(item => { map[item.id] = item; });
      list.forEach(item => { map[item.id] = item; });
      this.messages = Object.values(map).sort((a, b) => a.id - b.id);
      const last = this.messages[this.messages.length - 1];
      this.cursor = last ? last.id : this.cursor;
      this.$nextTick(this.scrollMsgToBottom);
    },
    scrollMsgToBottom() {
      const box = this.$refs.msgBox;
      if (!box) return;
      box.scrollTop = box.scrollHeight;
    },
    appendReply(content) {
      this.replyText = this.replyText ? `${this.replyText}\n${content}` : content;
    },
    async runFaqSearch() {
      const q = this.faqQuery.trim();
      if (!q) {
        this.faqResults = [];
        return;
      }
      this.faqSearchLoading = true;
      try {
        const { data } = await searchFaq(q);
        this.faqResults = data.data || [];
        const idSet = new Set(this.faqResults.map(r => r.id));
        this.faqSelectedIds = this.faqSelectedIds.filter(id => idSet.has(id));
      } catch (e) {
        this.$message.error('常见问题搜索失败');
      } finally {
        this.faqSearchLoading = false;
      }
    },
    async pushFaqToVisitor() {
      if (!this.currentConversationId || !this.faqSelectedIds.length) return;
      try {
        await pushConversationFaq(this.currentConversationId, { faq_ids: this.faqSelectedIds });
        this.$message.success('已推送给访客');
      } catch (e) {
        this.$message.error('推送失败');
      }
    },
    handleInputKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendReply();
      }
    },
    async sendReply() {
      if (!this.currentConversationId || !this.replyText.trim()) return;
      const content = this.replyText;
      const { data } = await replyConversation(this.currentConversationId, { content });
      this.replyText = '';
      if (data && data.data) this.upsertMessages([data.data]);
      await this.loadMessages();
    },
    async transfer() {
      if (!this.currentConversationId || !this.transferTarget) return;
      await transferConversation(this.currentConversationId, this.transferTarget);
      this.currentConversationId = null;
      this.messages = [];
      this.cursor = 0;
      await this.loadConversations();
    },
    async closeCurrent() {
      if (!this.currentConversationId) return;
      await closeConversation(this.currentConversationId);
      this.currentConversationId = null;
      this.messages = [];
      this.cursor = 0;
      await this.loadConversations();
    },
    async closeByRow(row) {
      await closeConversation(row.id);
      if (this.currentConversationId === row.id) {
        this.currentConversationId = null;
        this.messages = [];
        this.cursor = 0;
      }
      await this.loadConversations();
    },
    playNotifySound() {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        if (!this.notifyAudioCtx) this.notifyAudioCtx = new AudioCtx();
        const ctx = this.notifyAudioCtx;
        const now = ctx.currentTime;
        const gainNode = ctx.createGain();
        gainNode.gain.setValueAtTime(0.0001, now);
        gainNode.connect(ctx.destination);

        const playTone = (freq, start, duration, volume) => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, start);
          osc.connect(gainNode);
          gainNode.gain.setValueAtTime(0.0001, start);
          gainNode.gain.linearRampToValueAtTime(volume, start + 0.02);
          gainNode.gain.linearRampToValueAtTime(0.0001, start + duration);
          osc.start(start);
          osc.stop(start + duration + 0.02);
        };

        playTone(880, now, 0.45, 0.06);
        playTone(1046, now + 0.55, 0.45, 0.06);
      } catch (e) {}
    },
    startTitleFlash() {
      if (!this.originalTitle) this.originalTitle = document.title || '客服工作台';
      if (this.titleFlashTimer) return;
      this.titleFlashTimer = setInterval(() => {
        this.titleFlashToggle = !this.titleFlashToggle;
        document.title = this.titleFlashToggle ? '有新消息 - 客服工作台' : this.originalTitle;
      }, 800);
    },
    stopTitleFlash() {
      if (this.titleFlashTimer) {
        clearInterval(this.titleFlashTimer);
        this.titleFlashTimer = null;
      }
      if (this.originalTitle) document.title = this.originalTitle;
      this.titleFlashToggle = false;
    },
    markCurrentConversationRead() {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (!this.currentConversationId) return;
      this.$set(this.unreadMap, this.currentConversationId, 0);
    },
    connectWs() {
      const token = getToken();
      if (!token) return;
      if (this.ws) {
        try { this.ws.close(); } catch (e) {}
      }
      this.ws = new WebSocket(WS_URL);
      this.ws.onopen = () => {
        this.wsConnected = false;
        this.ws.send(JSON.stringify({ action: 'auth', role: 'agent', token }));
      };
      this.ws.onclose = () => {
        this.wsConnected = false;
        this.scheduleReconnect();
      };
      this.ws.onerror = () => {
        this.wsConnected = false;
      };
      this.ws.onmessage = async evt => {
        const e = JSON.parse(evt.data || '{}');
        if (e.type === 'auth_ok') {
          this.wsConnected = true;
          return;
        }
        if (e.type === 'error') {
          this.wsConnected = false;
          return;
        }
        if (e.type === 'message_new' && e.conversation_id === this.currentConversationId) {
          this.upsertMessages([e.payload]);
          return;
        }
        if (e.type === 'conversation_assigned' || e.type === 'conversation_closed') {
          await this.loadConversations();
          return;
        }
        if (e.type === 'message_new' && e.conversation_id !== this.currentConversationId) {
          this.$set(this.unreadMap, e.conversation_id, Number(this.unreadMap[e.conversation_id] || 0) + 1);
          this.playNotifySound();
          await this.loadConversations();
        }
      };
    },
    scheduleReconnect() {
      if (this.reconnectTimer) return;
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connectWs();
      }, 3000);
    },
    onPageChange() {
      this.$nextTick(this.highlightCurrentRow);
    },
    ensurePageInRange() {
      const totalPages = Math.max(1, Math.ceil(this.sortedFilteredConversations.length / this.pageSize));
      if (this.currentPage > totalPages) this.currentPage = totalPages;
      if (this.currentPage < 1) this.currentPage = 1;
    },
    locateConversationPage(conversationId) {
      const idx = this.sortedFilteredConversations.findIndex(item => item.id === conversationId);
      if (idx < 0) return;
      this.currentPage = Math.floor(idx / this.pageSize) + 1;
    },
    highlightCurrentRow() {
      if (!this.currentConversationId || !this.$refs.convTable) return;
      const row = this.paginatedConversations.find(item => item.id === this.currentConversationId);
      if (row) {
        this.$refs.convTable.setCurrentRow(row);
      }
    }
  },
  async created() {
    this.originalTitle = document.title || '客服工作台';
    document.addEventListener('visibilitychange', this.markCurrentConversationRead);
    await heartbeat();
    await Promise.all([
      this.loadConversations(),
      quickReplies().then(r => { this.quickReplyList = r.data.data || []; }),
      colleagues().then(r => { this.colleagueList = r.data.data || []; })
    ]);
    this.connectWs();
    this.timer = setInterval(() => {
      heartbeat().catch(() => {});
      this.loadMessages().catch(() => {});
      this.loadConversations().catch(() => {});
    }, 2500);
  },
  watch: {
    unreadTotal(val) {
      if (val > 0) this.startTitleFlash();
      else this.stopTitleFlash();
    },
    statusFilter() {
      this.currentPage = 1;
      this.$nextTick(this.highlightCurrentRow);
    },
    keyword() {
      this.currentPage = 1;
      this.$nextTick(this.highlightCurrentRow);
    },
    currentConversationId(val) {
      if (!val) return;
      this.locateConversationPage(val);
      this.$nextTick(this.highlightCurrentRow);
    }
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.ws) this.ws.close();
    this.stopTitleFlash();
    document.removeEventListener('visibilitychange', this.markCurrentConversationRead);
  }
};
</script>

<style scoped>
.workbench-v2 {
  min-height: calc(100vh - 100px);
}

.layout {
  display: flex;
  gap: 12px;
}

.side-menu {
  width: 170px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fff;
  height: calc(100vh - 130px);
  padding: 12px 10px;
}

.menu-title {
  font-size: 16px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 12px;
}

.menu-item {
  position: relative;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: #606266;
  margin-bottom: 6px;
  transition: all .2s;
}

.menu-item:hover {
  background: #f5f7fa;
}

.menu-item.active {
  background: #ecf5ff;
  color: #409eff;
  font-weight: 600;
}

.menu-badge {
  position: absolute;
  right: 10px;
  top: 10px;
}

.content {
  flex: 1;
}

.summary-row {
  margin-bottom: 12px;
}

.summary-card {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #ebeef5;
  padding: 10px 12px;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.04);
}

.summary-card .label {
  font-size: 12px;
  color: #909399;
}

.summary-card .value {
  margin-top: 4px;
  font-size: 20px;
  font-weight: 700;
  color: #303133;
}

.summary-card .value.online {
  color: #1f9d5c;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-subhint {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
  margin-left: 8px;
}

.quick-reply-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filters {
  display: grid;
  grid-template-columns: 1fr 120px;
  gap: 8px;
  margin-bottom: 10px;
}

.sort-tools {
  margin-bottom: 10px;
  display: flex;
  justify-content: flex-end;
}

.pager-wrap {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

.chat-card {
  min-height: 640px;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
}

.session-card {
  min-height: 640px;
  border-radius: 10px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.05);
}

.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.chat-title {
  font-size: 16px;
  font-weight: 700;
  color: #303133;
}

.chat-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.chat-actions {
  display: flex;
  gap: 8px;
}

.msg-box {
  height: 348px;
  overflow: auto;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 12px;
  background: #f8fafc;
}

.quick-reply-inline {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0 4px;
  border-top: 1px solid #ebeef5;
}

.quick-reply-label {
  flex-shrink: 0;
  font-size: 12px;
  color: #909399;
  line-height: 28px;
}

.quick-reply-chips {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.faq-inline {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0 4px;
  border-top: 1px solid #ebeef5;
}

.faq-inline-main {
  flex: 1;
  min-width: 0;
}

.faq-search-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.faq-check-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  max-height: 140px;
  overflow-y: auto;
}

.faq-push-row {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.faq-push-hint {
  font-size: 12px;
  color: #909399;
}

.msg-row {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 10px;
}

.msg-row.self {
  justify-content: flex-end;
}

.msg-bubble {
  max-width: 75%;
  border-radius: 10px;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
}

.msg-row.self .msg-bubble {
  background: #2f7cff;
  color: #fff;
  border-color: #2f7cff;
}

.hidden-file {
  display: none;
}

.msg-body {
  line-height: 1.55;
}

.msg-content {
  line-height: 1.55;
  word-break: break-word;
}

.msg-media.img {
  max-width: 220px;
  border-radius: 6px;
  display: block;
}

.msg-media.video {
  max-width: 240px;
  border-radius: 6px;
  display: block;
}

.msg-caption {
  margin-top: 6px;
  font-size: 13px;
}

.msg-file {
  text-decoration: underline;
  word-break: break-all;
}

.msg-row.self .msg-file {
  color: #e8f1ff;
}

.send-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.msg-time {
  margin-top: 4px;
  font-size: 11px;
  opacity: 0.7;
}

.input-area {
  margin-top: 10px;
}

.send-area {
  margin-top: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tips {
  font-size: 12px;
  color: #909399;
}

.unread-bell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 14px;
  animation: bell-blink 1s infinite;
}

.op-cell {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

@keyframes bell-blink {
  0% { transform: rotate(0deg) scale(1); opacity: 1; }
  25% { transform: rotate(-12deg) scale(1.08); opacity: 0.9; }
  50% { transform: rotate(12deg) scale(1.12); opacity: 1; }
  75% { transform: rotate(-8deg) scale(1.06); opacity: 0.9; }
  100% { transform: rotate(0deg) scale(1); opacity: 1; }
}
</style>
